import * as ImagePicker from 'expo-image-picker';
import AddImageIcon from '@assets/icons/add-image-icon.svg';
import CameraIcon from '@assets/icons/camera.svg';
import CheckmarkIcon from '@assets/icons/checkmark-thin.svg';
import ChevronFilters from '@assets/icons/chevron-filters.svg';
import i18n from '@/utils/i18n';
import ImageFromGalleryIcon from '@assets/icons/image-from-gallery.svg';
import moment from 'moment-timezone';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import RetakeIcon from '@assets/icons/retake.svg';
import TrashIcon from '@assets/icons/trash-small-thin.svg';
import {
  ActivityIndicator,
  Platform,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import {
  BaseActionSheet,
  DEFAULT_PADDING,
} from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { CONTAINER_PADDING_VERTICAL, TextArea } from '@/components/text-area';
import { createStyles } from '@/contexts/theme-context';
import {
  CREDIT_REQUEST_INVOICE_FRAGMENT,
  CREDIT_REQUEST_UOM_FRAGMENT,
} from '@/hooks/credit-requests/credit-request-queries';
import { CreditPolicy } from '@/components/credit-request/credit-policy';
import {
  CreditRequest_VariantPackInvoicesQuery,
  CreditRequest_VariantPackInvoicesQueryVariables,
  GetCreditRequestReasonFieldsFragment,
} from '@/types';
import {
  CreditRequestBatchLineItem,
  InvoiceListOption,
} from '@/contexts/credit-request-context-types';
import { CreditRequestCamera } from '@/components/credit-request/credit-request-camera';
import {
  CreditRequestReasonSelection,
  isCreditRequestReasonDisabled,
  useCreditRequestReasons,
} from '@/components/credit-request/credit-request-reason-selection';
import {
  DEFAULT_CURRENCY_CODE,
  formatCurrency,
  roundMicrosToNearestWholeCent,
} from '@/utils/money';
import { formatDateWithYear } from '@/utils/date';
import { FormExitConfirmation } from '@/components/form-exit-confirmation';
import { FormInputContainer } from '@/components/form';
import { getTranslationValue } from '@/utils/translations';
import { gql } from '@apollo/client';
import { INPUT_HEIGHT } from '@/components/text-input';
import { ItemListOption } from '@/contexts/item-context-types';
import { KeyboardAwareScrollView } from '@/components/with-keyboard-aware';
import { List } from '@/components/list';
import { ListItemSeparator, ListOption } from '@/components/list-item';
import { OrderableItemListItemImage } from '@/components/orders/orderable-item-list-item-image';
import { QuantitySelector } from '@/components/quantity-selector';
import { SelectInput } from '@/components/select-input';
import { SvgProps } from 'react-native-svg';
import { ThemedSVGProps, TYPOGRAPHY } from '@/styles';
import { Typography } from '@usepepper/ui/atoms/typography';
import { UPLOAD_PREFIXES, uploadPublicImage } from '@/services/s3';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useFormik } from 'formik';
import { useIsOverridingIneligibleItem } from '@/hooks/use-is-overriding-ineligible-item';
import { useItemDisplayOptions } from '@/contexts/new-order-context';
import { useLastUsedCreditRequestReason } from '@/hooks/credit-requests/use-last-used-credit-request-reason';
import { useOfflineCreditRequestData } from '@/hooks/use-offline-data';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { usePermissionDeniedAlert } from '@/hooks/use-permission-denied-alert';
import { useRequestCamera } from '@/hooks/use-request-camera';
import { useToasts } from '@/contexts/toast-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';
import { v4 as uuidv4 } from 'uuid';

export const VP_INVOICES_FOR_CREDIT_REQUEST_QUERY = gql`
  query CreditRequest_VariantPackInvoices(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $variantPackUUID: uuid!
  ) {
    getVariantPackInvoicesForCreditRequest(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      variant_pack_id: $variantPackUUID
    ) {
      invoices {
        ...CreditRequest_SearchVariantPacksInvoices
      }
      other_creditable_uoms {
        ...CreditRequest_SearchOtherUOMs
      }
    }
  }

  ${CREDIT_REQUEST_INVOICE_FRAGMENT}
  ${CREDIT_REQUEST_UOM_FRAGMENT}
`;

const ADDITIONAL_NOTES_LINES = 5;
const MAX_TOTAL_PHOTO_LIMIT = 3;

enum CREDIT_REQUEST_IMAGES_ACTIONS {
  Delete = 'Delete',
  Retake = 'Retake',
  UploadFromGallery = 'UploadFromGallery',
  UseCamera = 'UseCamera',
}

type PackListOption = ListOption<{
  quantity: number;
}>;

type ImagesActionListOption = ListOption<{
  Icon: React.ComponentType<SvgProps>;
  iconProps?: ThemedSVGProps;
  onPress: () => void | Promise<void>;
}>;

interface CreditRequestFormValues {
  batchItemUUID: string;
  invoice: InvoiceListOption | null;
  note: string;
  pack: PackListOption | null;
  photos: string[];
  priceInMicros: number;
  quantity: number;
  reason: GetCreditRequestReasonFieldsFragment | null;
}

export interface CreditRequestFormContentHandle {
  attemptClose: (onConfirmedClose: () => void) => void;
}

export interface CreditRequestFormContentProps {
  confirmOnExit?: boolean;
  eligibleForCredit: boolean;
  existingBatchLineItem?: CreditRequestBatchLineItem;
  guaranteed?: boolean;
  invoiceQuantityMap?: Record<string, number>;
  itemDetail: ItemListOption;
  multiRequest?: boolean;
  onShowCameraChange?: (showCamera: boolean) => void;
  onSuccess: (batchLineItem: CreditRequestBatchLineItem) => void;
  orderInvoiceUUID?: string;
}

export const CreditRequestFormContent = forwardRef<
  CreditRequestFormContentHandle,
  CreditRequestFormContentProps
>(function CreditRequestFormContent(
  {
    confirmOnExit,
    eligibleForCredit,
    existingBatchLineItem,
    guaranteed,
    invoiceQuantityMap,
    itemDetail,
    multiRequest,
    onShowCameraChange,
    onSuccess,
    orderInvoiceUUID,
  },
  ref,
) {
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [pendingExitCallback, setPendingExitCallback] = useState<
    (() => void) | null
  >(null);
  const [selectingPack, setSelectingPack] = useState(false);
  const [selectingReason, setSelectingReason] = useState(false);
  const [selectingInvoice, setSelectingInvoice] = useState(false);
  const [showIneligibleItemConfirmation, setShowIneligibleItemConfirmation] =
    useState(false);
  const [showCameraLibraryOptions, setShowCameraLibraryOptions] =
    useState(false);
  const [updatingPhoto, setUpdatingPhoto] = useState<string | null>(null);
  const [showCamera, setShowCameraState] = useState(false);
  const setShowCamera = useCallback(
    (next: boolean) => {
      setShowCameraState(next);
      onShowCameraChange?.(next);
    },
    [onShowCameraChange],
  );

  const metadata = itemDetail.metadata!;

  const {
    display_guarantee_information: displayGuaranteeInformation,
    credit_policy: creditPolicy,
  } = useDynamicConfigValue('credit_request_config') ?? {};
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const isOfflineModeActive = useOfflineModeActive();
  const { data: offlineCreditRequestData } = useOfflineCreditRequestData(
    restaurantUUID,
    supplierUUID,
  );
  const isOverridingIneligibleItem =
    useIsOverridingIneligibleItem(eligibleForCredit);
  const showPermissionDeniedAlert = usePermissionDeniedAlert();
  const { showErrorToast } = useToasts();
  const { requestCamera } = useRequestCamera();
  const { creditRequestReasons } = useCreditRequestReasons({
    restaurantUUID,
    supplierUUID,
  });
  const { lastUsedReasonUUID, recordReasonUsed } =
    useLastUsedCreditRequestReason(supplierUUID);

  const { data, loading } = useWrappedQuery<
    CreditRequest_VariantPackInvoicesQuery,
    CreditRequest_VariantPackInvoicesQueryVariables
  >(VP_INVOICES_FOR_CREDIT_REQUEST_QUERY, {
    fetchPolicy: 'network-only',
    skip: isOfflineModeActive,
    variables: {
      restaurantUUID,
      supplierUUID,
      variantPackUUID: metadata.variantPackUUID,
    },
  });

  const offlineVariantPackCreditRequestData = useMemo(
    () =>
      isOfflineModeActive
        ? offlineCreditRequestData?.getCreditRequestVariantPackEligibility.variant_pack_credit_request_data.find(
            (data) => data.variant_pack!.uuid === metadata.variantPackUUID,
          )
        : null,
    [isOfflineModeActive, offlineCreditRequestData, metadata.variantPackUUID],
  );

  const invoices = useMemo(() => {
    const offlineInvoiceData =
      offlineVariantPackCreditRequestData?.latest_invoice;

    return offlineInvoiceData
      ? [offlineInvoiceData]
      : (data?.getVariantPackInvoicesForCreditRequest.invoices ?? []);
  }, [
    data?.getVariantPackInvoicesForCreditRequest.invoices,
    offlineVariantPackCreditRequestData?.latest_invoice,
  ]);

  const originalInvoice = useMemo(
    () =>
      orderInvoiceUUID
        ? invoices.find((invoice) => invoice.id === orderInvoiceUUID)
        : undefined,
    [invoices, orderInvoiceUUID],
  );

  const otherCreditableUOMs = useMemo(
    () =>
      offlineVariantPackCreditRequestData
        ? offlineVariantPackCreditRequestData?.other_creditable_uoms
        : (data?.getVariantPackInvoicesForCreditRequest.other_creditable_uoms ??
          []),
    [
      data?.getVariantPackInvoicesForCreditRequest.other_creditable_uoms,
      offlineVariantPackCreditRequestData,
    ],
  );

  const isGuaranteed = displayGuaranteeInformation ? guaranteed : undefined;
  const hasGuaranteeInformation = isGuaranteed !== undefined;

  const windowDimensions = useWindowDimensions();
  const { currencyCode } = useVariantPackInfoContext();
  const { hidePlaceholderImages } = useItemDisplayOptions();
  const [styles, theme] = useStyles();

  const { dirty, isSubmitting, setFieldValue, submitForm, values } =
    useFormik<CreditRequestFormValues>({
      initialValues: {
        batchItemUUID: existingBatchLineItem?.uuid ?? uuidv4(),
        invoice: existingBatchLineItem?.invoice ?? null,
        note: existingBatchLineItem?.notes ?? '',
        pack: {
          metadata: {
            quantity: 1,
          },
          title: metadata.packUnit,
          value: metadata.packUnit,
        },
        photos: existingBatchLineItem?.photoURLs ?? [],
        priceInMicros:
          existingBatchLineItem?.priceInMicros ??
          metadata.unitPriceInMicros ??
          0,
        quantity: existingBatchLineItem?.quantity ?? 1,
        reason: existingBatchLineItem?.creditRequestReason ?? null,
      },
      onSubmit: async (values) => {
        const imageUrls: string[] = [];
        if (values.photos.length > 0) {
          for (const photoURI of values.photos) {
            const imageUrl = await uploadPublicImage(
              uuidv4(),
              photoURI,
              UPLOAD_PREFIXES.CreditRequests,
            );
            imageUrls.push(imageUrl);
          }
        }
        onSuccess({
          creditRequestReason: values.reason!,
          eligibleForCredit,
          fulfillmentDate,
          guaranteed: displayGuaranteeInformation ? guaranteed : undefined,
          invoice: values.invoice,
          item: itemDetail,
          maxQuantity,
          notes: values.note,
          orderInvoiceId: values.invoice?.metadata?.uuid,
          photoURLs: imageUrls.length > 0 ? imageUrls : undefined,
          priceInMicros: values.priceInMicros,
          quantity: values.quantity,
          selectedPack: values.pack?.title ?? metadata.packUnit,
          uomOverride:
            values.pack?.value && values.pack.value !== metadata.packUnit
              ? `${values.pack.value}`
              : undefined,
          uuid: values.batchItemUUID,
        });
      },
    });

  const fulfillmentDate =
    values.invoice?.metadata?.date ||
    originalInvoice?.date ||
    invoices[0]?.date;

  useImperativeHandle(
    ref,
    () => ({
      attemptClose: (onConfirmedClose: () => void) => {
        if (confirmOnExit && dirty) {
          setPendingExitCallback(() => onConfirmedClose);
          setShowExitConfirmation(true);
        } else {
          onConfirmedClose();
        }
      },
    }),
    [confirmOnExit, dirty],
  );

  const onReasonChange = useCallback(
    (selection: GetCreditRequestReasonFieldsFragment) => {
      setFieldValue('reason', selection);
      recordReasonUsed(selection.uuid);

      if (selection.invoice_required || originalInvoice) {
        const invoice = originalInvoice
          ? originalInvoice
          : invoices.length === 1
            ? invoices[0]
            : null;

        if (!invoice) {
          setFieldValue('invoice', null);
        } else {
          const invoiceValue: InvoiceListOption = {
            metadata: {
              availableQuantityForCredit: invoice.available_quantity_for_credit,
              date: invoice.date,
              priceInMicros: invoice.unit_price_in_micros,
              quantity: invoice.quantity,
              uuid: invoice.id,
            },
            title: formatDateWithYear(invoice.date),
            value: invoice.number,
          };
          setFieldValue('invoice', invoiceValue);
          setFieldValue('priceInMicros', invoice.unit_price_in_micros);
          if (values.quantity > invoice.quantity) {
            setFieldValue('quantity', invoice.quantity);
          }
        }
      } else {
        const invoiceValue: InvoiceListOption = {
          metadata: {
            date: '',
            priceInMicros: metadata.unitPriceInMicros ?? 0,
            quantity: undefined,
          },
          title: i18n.t('credit_request.form.no_invoice.title'),
          value: '',
        };
        setFieldValue('invoice', invoiceValue);
      }

      setSelectingReason(false);
    },
    [
      invoices,
      metadata.unitPriceInMicros,
      originalInvoice,
      recordReasonUsed,
      setFieldValue,
      values.quantity,
    ],
  );

  const appliedDefaultReasonRef = useRef(false);
  useEffect(() => {
    if (
      existingBatchLineItem ||
      appliedDefaultReasonRef.current ||
      values.reason ||
      !lastUsedReasonUUID
    ) {
      return;
    }

    const defaultReason = creditRequestReasons.find(
      (reason) => reason.uuid === lastUsedReasonUUID,
    );

    if (!defaultReason) {
      return;
    }

    appliedDefaultReasonRef.current = true;

    if (
      !isCreditRequestReasonDisabled(defaultReason, {
        guaranteed: isGuaranteed,
        invoiceQuantityMap,
        invoices,
        isOverridingIneligibleItem,
        mostRecentInvoiceDate: fulfillmentDate,
      })
    ) {
      onReasonChange(defaultReason);
    }
  }, [
    creditRequestReasons,
    existingBatchLineItem,
    fulfillmentDate,
    invoiceQuantityMap,
    invoices,
    isGuaranteed,
    isOverridingIneligibleItem,
    lastUsedReasonUUID,
    onReasonChange,
    values.reason,
  ]);

  const onInvoiceChange = useCallback(
    (option: InvoiceListOption) => {
      setFieldValue('invoice', option);
      const packValue: PackListOption = {
        metadata: {
          quantity: 1,
        },
        title: metadata.packUnit,
        value: metadata.packUnit,
      };
      setFieldValue('pack', packValue);
      setFieldValue('quantity', 1);
      if (option.metadata?.priceInMicros) {
        setFieldValue('priceInMicros', option.metadata.priceInMicros);
      }

      setSelectingInvoice(false);
    },
    [setFieldValue, metadata.packUnit],
  );

  const getMaxQuantityForPack = useCallback(
    (packUnitQuantity: number | undefined) => {
      const selectedInvoiceAvailableQuantity =
        values.invoice?.metadata?.availableQuantityForCredit ??
        values.invoice?.metadata?.quantity ??
        0;

      let selectedInvoiceCurrentQuantity = 0;

      if (
        invoiceQuantityMap &&
        values.invoice?.metadata?.uuid &&
        invoiceQuantityMap[values.invoice.metadata.uuid]
      ) {
        selectedInvoiceCurrentQuantity =
          invoiceQuantityMap[values.invoice.metadata.uuid];
      }

      const selectedInvoiceMaxQuantity =
        selectedInvoiceAvailableQuantity - selectedInvoiceCurrentQuantity;
      if (selectedInvoiceMaxQuantity) {
        return packUnitQuantity
          ? selectedInvoiceMaxQuantity * packUnitQuantity
          : selectedInvoiceMaxQuantity;
      }
      return undefined;
    },
    [invoiceQuantityMap, values.invoice],
  );

  const onPackUnitChange = useCallback(
    (option: PackListOption) => {
      setFieldValue('pack', option);
      const basePriceInMicros =
        values.invoice?.metadata?.priceInMicros ??
        metadata.unitPriceInMicros ??
        0;
      if (
        option.metadata?.quantity &&
        option.metadata.quantity !== 0 &&
        basePriceInMicros
      ) {
        setFieldValue(
          'priceInMicros',
          roundMicrosToNearestWholeCent(
            basePriceInMicros / option.metadata.quantity,
          ),
        );
      }

      const newMaxQuantity = getMaxQuantityForPack(option.metadata?.quantity);
      if (newMaxQuantity !== undefined && values.quantity > newMaxQuantity) {
        setFieldValue('quantity', newMaxQuantity);
      }

      setSelectingPack(false);
    },
    [
      getMaxQuantityForPack,
      metadata.unitPriceInMicros,
      setFieldValue,
      values.invoice?.metadata?.priceInMicros,
      values.quantity,
    ],
  );

  const onQuantityChange = useCallback(
    (quantity: number) => {
      setFieldValue('quantity', quantity);
    },
    [setFieldValue],
  );

  const maxQuantity = useMemo(
    () => getMaxQuantityForPack(values.pack?.metadata?.quantity),
    [getMaxQuantityForPack, values.pack?.metadata?.quantity],
  );

  const invoiceListOptions: InvoiceListOption[] = useMemo(() => {
    const currentInvoices = invoices.map((invoice) => ({
      metadata: {
        availableQuantityForCredit: invoice.available_quantity_for_credit,
        date: invoice.date,
        priceInMicros: invoice.unit_price_in_micros,
        quantity: invoice.quantity,
        uuid: invoice.id,
      },
      title: formatDateWithYear(invoice.date),
      value: invoice.number,
    }));
    if (!values.reason?.invoice_required) {
      return [
        {
          metadata: {
            date: '',
            priceInMicros: metadata.unitPriceInMicros ?? 0,
            quantity: undefined,
          },
          title: i18n.t('credit_request.form.no_invoice.title'),
          value: '',
        },
        ...currentInvoices,
      ];
    }
    return currentInvoices;
  }, [invoices, metadata.unitPriceInMicros, values.reason?.invoice_required]);

  const packListOptions: PackListOption[] = useMemo(
    () =>
      [
        {
          quantity: 1,
          uom: metadata.packUnit,
        },
        ...(otherCreditableUOMs ?? []),
      ].map(({ uom, quantity }) => ({
        metadata: {
          quantity,
        },
        title: uom,
        value: uom,
      })),
    [otherCreditableUOMs, metadata.packUnit],
  );

  const canSelectUOM = (otherCreditableUOMs ?? []).length > 0;
  const renderItemDetails = invoices.length === 0 || Boolean(values.invoice);

  const submitFormDisabled =
    !values.reason ||
    (eligibleForCredit && values.reason.invoice_required && !values.invoice) ||
    isSubmitting;

  const isInvoiceDisabled = useCallback(
    (option: InvoiceListOption) => {
      if (isOverridingIneligibleItem) {
        return false;
      }

      const availableQuantityForCredit =
        option.metadata?.availableQuantityForCredit;
      if (availableQuantityForCredit === 0) {
        return true;
      }

      const invoiceUUID = option.metadata?.uuid;
      if (
        invoiceQuantityMap &&
        Object.keys(invoiceQuantityMap).includes(invoiceUUID || '')
      ) {
        const currentQuantity = invoiceUUID
          ? invoiceQuantityMap[invoiceUUID]
          : undefined;
        return currentQuantity === availableQuantityForCredit;
      }

      const eligibilityPeriod = values.reason?.request_period_days;
      const invoiceDate = option.metadata?.date;
      if (!eligibilityPeriod || !invoiceDate) {
        return false;
      }

      const invoiceDateMoment = moment(invoiceDate);
      return invoiceDateMoment
        .add(eligibilityPeriod, 'days')
        .isSameOrBefore(moment(), 'day');
    },
    [
      invoiceQuantityMap,
      isOverridingIneligibleItem,
      values.reason?.request_period_days,
    ],
  );

  const addPhotos = useCallback(
    (newPhotos: string[]) => {
      if (updatingPhoto) {
        setFieldValue('photos', [
          ...values.photos.filter((photo) => photo !== updatingPhoto),
          ...newPhotos,
        ]);
        setUpdatingPhoto(null);
      } else {
        setFieldValue('photos', [...values.photos, ...newPhotos]);
      }
    },
    [setFieldValue, updatingPhoto, values.photos],
  );

  const deletePhoto = useCallback(() => {
    setFieldValue(
      'photos',
      values.photos.filter((photo) => photo !== updatingPhoto),
    );
    setUpdatingPhoto(null);
  }, [setFieldValue, updatingPhoto, values.photos]);

  const enforcePhotoLimit = useCallback(() => {
    if (values.photos.length >= MAX_TOTAL_PHOTO_LIMIT) {
      showErrorToast(
        i18n.t('camera.photo_limit_reached', {
          photoLimit: MAX_TOTAL_PHOTO_LIMIT,
        }),
      );
      return true;
    }
    return false;
  }, [values.photos.length, showErrorToast]);

  const pickImagesFromMediaLibrary = useCallback(async () => {
    if (enforcePhotoLimit()) {
      setShowCameraLibraryOptions(false);
      return;
    }
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    switch (status) {
      case ImagePicker.PermissionStatus.GRANTED: {
        const result = await ImagePicker.launchImageLibraryAsync({
          allowsMultipleSelection: true,
          exif: false,
          mediaTypes: 'images',
          orderedSelection: true,
          quality: 0.5,
          selectionLimit: MAX_TOTAL_PHOTO_LIMIT - values.photos.length,
        });

        if (!result.canceled) {
          const selectedUris = result.assets.map((asset) => asset.uri);
          addPhotos(selectedUris);
        }
        break;
      }
      case ImagePicker.PermissionStatus.DENIED: {
        showPermissionDeniedAlert(i18n.t('camera.media_access_required'));
        break;
      }
    }
    setShowCameraLibraryOptions(false);
  }, [
    enforcePhotoLimit,
    addPhotos,
    showPermissionDeniedAlert,
    values.photos.length,
  ]);

  const handleUseCamera = useCallback(async () => {
    const status = await requestCamera();
    if (status === 'granted') {
      setShowCamera(true);
    }
  }, [requestCamera, setShowCamera]);

  const singleImageActions: ImagesActionListOption[] = useMemo(
    () => [
      {
        metadata: {
          Icon: RetakeIcon,
          iconProps: {
            stroke: theme.sk.color.icon.primary,
          },
          onPress: handleUseCamera,
        },
        title: i18n.t('retake'),
        value: CREDIT_REQUEST_IMAGES_ACTIONS.Retake,
      },
      {
        metadata: {
          Icon: TrashIcon,
          iconProps: {
            stroke: theme.sk.color.icon.negative.primary,
          },
          onPress: deletePhoto,
        },
        title: i18n.t('delete'),
        value: CREDIT_REQUEST_IMAGES_ACTIONS.Delete,
      },
    ],
    [
      deletePhoto,
      handleUseCamera,
      theme.sk.color.icon.negative.primary,
      theme.sk.color.icon.primary,
    ],
  );

  const cameraLibraryActions: ImagesActionListOption[] = useMemo(
    () => [
      ...(Platform.OS === 'web'
        ? []
        : [
            {
              metadata: {
                Icon: CameraIcon,
                iconProps: {
                  stroke: theme.sk.color.icon.primary,
                },
                onPress: handleUseCamera,
              },
              title: i18n.t('use_camera'),
              value: CREDIT_REQUEST_IMAGES_ACTIONS.UseCamera,
            },
          ]),
      {
        metadata: {
          Icon: ImageFromGalleryIcon,
          iconProps: {
            stroke: theme.sk.color.icon.primary,
          },
          onPress: pickImagesFromMediaLibrary,
        },
        title: i18n.t('upload_from_gallery'),
        value: CREDIT_REQUEST_IMAGES_ACTIONS.UploadFromGallery,
      },
    ],
    [handleUseCamera, pickImagesFromMediaLibrary, theme.sk.color.icon.primary],
  );

  if (showCamera) {
    return (
      <CreditRequestCamera
        addPhotos={addPhotos}
        handleClose={() => {
          if (updatingPhoto) {
            setUpdatingPhoto(null);
          }
          setShowCamera(false);
        }}
      />
    );
  }

  return (
    <>
      <KeyboardAwareScrollView
        contentContainerStyle={styles.contentContainer}
        extraSpacing={40}
        keyboardDismissMode="on-drag"
      >
        <View style={styles.content}>
          {loading ? (
            <View style={styles.loadingWrapper}>
              <ActivityIndicator />
            </View>
          ) : (
            <>
              <View collapsable={false}>
                <View style={styles.container}>
                  <OrderableItemListItemImage
                    alt={metadata.displayName}
                    hidePlaceholderImages={hidePlaceholderImages}
                    imageSize={40}
                    imageURLs={metadata.photoURLs ?? []}
                    style={styles.imagePreview}
                  />
                  <View style={styles.details}>
                    <Typography numberOfLines={1} variant="paragraph-base-600">
                      {metadata.displayName}
                    </Typography>
                    <View style={styles.creditItemInfo}>
                      {hasGuaranteeInformation ? (
                        <Typography
                          color={
                            guaranteed
                              ? theme.sk.color.text.success
                              : theme.sk.color.text.warning
                          }
                          variant="paragraph-sm-600"
                        >
                          {i18n.t(
                            guaranteed
                              ? 'credit_request.guaranteed'
                              : 'credit_request.not_guaranteed',
                          )}
                        </Typography>
                      ) : null}
                      <Typography
                        color={theme.sk.color.text.tertiary}
                        variant="paragraph-sm-600"
                      >
                        {hasGuaranteeInformation ? ' • ' : ''}
                        {fulfillmentDate
                          ? i18n.t('fulfilled_date', {
                              date: formatDateWithYear(fulfillmentDate),
                            })
                          : i18n.t('never_fulfilled')}
                      </Typography>
                    </View>
                  </View>
                </View>
                <FormInputContainer>
                  <SelectInput
                    inputContainerStyle={styles.inputContainer}
                    label={
                      !values.reason
                        ? i18n.t(
                            'credit_request.form.select_reason.placeholder',
                          )
                        : ''
                    }
                    onPress={() => setSelectingReason(true)}
                    rightElement={
                      <ChevronFilters
                        fill={theme.colors.primary}
                        height={8}
                        width={14}
                      />
                    }
                    testID="credit-request-reason-selection"
                    value={
                      values.reason
                        ? getTranslationValue(values.reason.title)
                        : ''
                    }
                  />
                </FormInputContainer>
                {!orderInvoiceUUID && invoices.length > 0 ? (
                  <FormInputContainer>
                    <SelectInput
                      inputContainerStyle={styles.inputContainer}
                      label={
                        !values.invoice
                          ? i18n.t('credit_request.form.select_invoice')
                          : ''
                      }
                      onPress={() => setSelectingInvoice(true)}
                      rightElement={
                        <ChevronFilters
                          fill={theme.colors.primary}
                          height={8}
                          width={14}
                        />
                      }
                      testID="credit-request-invoice-selection"
                      value={
                        values.invoice?.value
                          ? i18n.t('invoice_number', {
                              number: values.invoice.value,
                            })
                          : (values.invoice?.title ?? '')
                      }
                    />
                  </FormInputContainer>
                ) : null}
                {renderItemDetails ? (
                  <FormInputContainer>
                    <View
                      accessibilityLabel="Select Item"
                      style={styles.packSelectionContainer}
                    >
                      <SelectInput
                        inputContainerStyle={styles.borderlessInputContainer}
                        label={
                          !values.pack
                            ? i18n.t(
                                'credit_request.form.select_item.place_holder',
                              )
                            : ''
                        }
                        onPress={() => {
                          if (canSelectUOM) {
                            setSelectingPack(true);
                          }
                        }}
                        rightElement={
                          canSelectUOM ? (
                            <ChevronFilters
                              fill={theme.colors.primary}
                              height={8}
                              width={14}
                            />
                          ) : null
                        }
                        testID="credit-request-pack-selection"
                        value={
                          values.pack
                            ? `${values.pack.title} • ${itemDetail.metadata?.externalID}`
                            : ''
                        }
                      />
                      <ListItemSeparator style={styles.separator} />
                      <View style={styles.itemSubtextContainer}>
                        <View style={styles.priceContainer}>
                          <Text
                            style={TYPOGRAPHY.medium}
                          >{`${i18n.t('unit_cost')} `}</Text>
                          <Text style={TYPOGRAPHY.pSemiBold}>
                            {formatCurrency(
                              values.priceInMicros,
                              currencyCode ?? DEFAULT_CURRENCY_CODE,
                            )}
                          </Text>
                        </View>
                        <View style={styles.quantitySelector}>
                          <QuantitySelector
                            canDecrementToZero={false}
                            isFocused={false}
                            maxQuantity={maxQuantity}
                            onChange={(_, quantity) => {
                              onQuantityChange(quantity);
                            }}
                            quantity={values.quantity}
                          />
                        </View>
                      </View>
                    </View>
                  </FormInputContainer>
                ) : null}
                <FormInputContainer>
                  <TextArea
                    accessibilityLabel="Additional Notes"
                    containerStyle={[
                      styles.inputContainer,
                      styles.textAreaContainer,
                    ]}
                    editable
                    numberOfLines={ADDITIONAL_NOTES_LINES}
                    onChangeText={(text) => setFieldValue('note', text)}
                    placeholder={i18n.t(
                      'credit_request.form.additional_notes.place_holder',
                    )}
                    returnKeyType="done"
                    scrollEnabled={false}
                    submitBehavior="blurAndSubmit"
                    testID="additional-notes"
                    value={values.note}
                  />
                  <View style={styles.itemImagePreviews}>
                    {values.photos.length > 0
                      ? values.photos.map((photo, index) => (
                          <TouchableOpacity
                            key={photo}
                            onPress={() => setUpdatingPhoto(photo)}
                          >
                            <CachedImage
                              alt={`Item image #${index}`}
                              contentFit="cover"
                              source={{ uri: photo }}
                              style={styles.itemImagePreview}
                              testID={`credit-request-item-image-${index}`}
                            />
                          </TouchableOpacity>
                        ))
                      : null}
                    {values.photos.length < MAX_TOTAL_PHOTO_LIMIT ? (
                      <TouchableOpacity
                        onPress={() => setShowCameraLibraryOptions(true)}
                        style={styles.addImageButton}
                        testID="credit-request-add-image-button"
                      >
                        <AddImageIcon stroke={theme.sk.color.icon.primary} />
                      </TouchableOpacity>
                    ) : null}
                  </View>
                </FormInputContainer>
              </View>
              <Button
                accessibilityLabel="Form Button"
                disabled={submitFormDisabled}
                loading={isSubmitting}
                onPress={async () => {
                  if (isOverridingIneligibleItem) {
                    setShowIneligibleItemConfirmation(true);
                  } else {
                    await submitForm();
                  }
                }}
                trackingData={{ name: 'credit_request_form.submit' }}
              >
                {multiRequest ? i18n.t('done') : i18n.t('submit')}
              </Button>
            </>
          )}
        </View>
        {showIneligibleItemConfirmation ? (
          <BaseActionSheet
            isVisible
            onClose={() => setShowIneligibleItemConfirmation(false)}
          >
            <View style={styles.ineligibleItemConfirmationWrapper}>
              <View style={styles.ineligibleItemConfirmationHeader}>
                <Typography variant="heading-lg">
                  {i18n.t('ineligible_item.title')}
                </Typography>
              </View>
              <CreditPolicy creditPolicy={creditPolicy ?? []} />
            </View>
            <Button
              onPress={async () => {
                setShowIneligibleItemConfirmation(false);
                await submitForm();
              }}
              trackingData={{
                name: 'ineligible_item_confirmation_button_ok',
              }}
            >
              {i18n.t('save_anyway')}
            </Button>
          </BaseActionSheet>
        ) : null}
        {selectingReason ? (
          <BaseActionSheet isVisible onClose={() => setSelectingReason(false)}>
            <CreditRequestReasonSelection
              eligibleForCredit={eligibleForCredit}
              guaranteed={isGuaranteed}
              invoiceQuantityMap={invoiceQuantityMap}
              invoices={invoices}
              mostRecentInvoiceDate={fulfillmentDate}
              onSelectReason={onReasonChange}
            />
          </BaseActionSheet>
        ) : null}
        {selectingInvoice ? (
          <BaseActionSheet isVisible onClose={() => setSelectingInvoice(false)}>
            <List
              data={invoiceListOptions}
              ListHeaderComponent={
                <>
                  <Typography variant="heading-lg">
                    {i18n.t('credit_request.form.select_invoice')}
                  </Typography>
                  {values.reason?.request_period_days ? (
                    <Typography
                      color={theme.sk.color.text.tertiary}
                      variant="paragraph-base"
                    >
                      {i18n.t(
                        'credit_request.form.select_invoice.description',
                        {
                          days: values.reason.request_period_days,
                        },
                      )}
                    </Typography>
                  ) : null}
                </>
              }
              ListHeaderComponentStyle={styles.listHeader}
              listItemStyle={(option) =>
                isInvoiceDisabled(option) ? styles.disabledListItem : null
              }
              onSelect={(option) => {
                if (!isInvoiceDisabled(option)) {
                  onInvoiceChange(option);
                }
              }}
              renderMainContent={(option) => (
                <View style={styles.invoiceItem}>
                  <View style={styles.flex}>
                    <Typography variant="paragraph-base-600">
                      {option.title}
                    </Typography>
                    {Boolean(option.value) ? (
                      <Text numberOfLines={1}>
                        {itemDetail.metadata?.packUnit ? (
                          <Typography
                            textTransform="capitalize"
                            variant="paragraph-md"
                          >
                            {`${itemDetail.metadata.packUnit} • `}
                          </Typography>
                        ) : null}
                        <Typography variant="paragraph-md">
                          {option.value}
                        </Typography>
                      </Text>
                    ) : (
                      <Typography variant="paragraph-md">
                        {i18n.t('credit_request.form.no_invoice.description')}
                      </Typography>
                    )}
                  </View>
                  {Boolean(option.value) ? (
                    <View collapsable={false}>
                      <Text style={styles.textRight}>
                        <Typography
                          color={theme.sk.color.text.primary}
                          variant="paragraph-base"
                        >
                          {formatCurrency(
                            (option.metadata!.quantity ?? 0) *
                              option.metadata!.priceInMicros,
                            currencyCode ?? DEFAULT_CURRENCY_CODE,
                          )}
                        </Typography>
                      </Text>

                      <Typography variant="paragraph-md">{`${option.metadata!.quantity} x ${formatCurrency(
                        option.metadata!.priceInMicros,
                        currencyCode ?? DEFAULT_CURRENCY_CODE,
                      )}`}</Typography>
                    </View>
                  ) : null}
                </View>
              )}
              style={[
                styles.list,
                { maxHeight: Math.floor(windowDimensions.height * 0.6) },
              ]}
            />
          </BaseActionSheet>
        ) : null}
        {selectingPack ? (
          <BaseActionSheet isVisible onClose={() => setSelectingPack(false)}>
            <List
              data={packListOptions}
              ListHeaderComponent={
                <Typography variant="heading-lg">
                  {i18n.t('credit_request.form.change_pack_unit')}
                </Typography>
              }
              ListHeaderComponentStyle={styles.listHeader}
              onSelect={onPackUnitChange}
              renderRightComponent={(option) =>
                option.value === values.pack?.value ? (
                  <CheckmarkIcon stroke={theme.sk.color.icon.primary} />
                ) : null
              }
              style={[
                styles.list,
                { maxHeight: Math.floor(windowDimensions.height * 0.6) },
              ]}
            />
          </BaseActionSheet>
        ) : null}
        {showCameraLibraryOptions ? (
          <BaseActionSheet
            contentStyle={styles.actionSheetContent}
            isVisible
            onClose={() => setShowCameraLibraryOptions(false)}
          >
            <List<ImagesActionListOption>
              data={cameraLibraryActions}
              onSelect={(item) => {
                item.metadata?.onPress();
                setShowCameraLibraryOptions(false);
              }}
              renderLeftComponent={(item) => {
                const { Icon, iconProps } = item.metadata!;
                return <Icon height={24} width={24} {...iconProps} />;
              }}
            />
          </BaseActionSheet>
        ) : null}
        {updatingPhoto ? (
          <BaseActionSheet
            contentStyle={styles.actionSheetContent}
            isVisible
            onClose={() => setUpdatingPhoto(null)}
          >
            <List<ImagesActionListOption>
              data={singleImageActions}
              listItemTitleStyle={(item) => {
                if (item.value === CREDIT_REQUEST_IMAGES_ACTIONS.Delete) {
                  return {
                    color: theme.sk.color.text.error,
                  };
                }
              }}
              onSelect={(item) => {
                item.metadata?.onPress();
              }}
              renderLeftComponent={(item) => {
                const { Icon, iconProps } = item.metadata!;
                return <Icon height={24} width={24} {...iconProps} />;
              }}
            />
          </BaseActionSheet>
        ) : null}
      </KeyboardAwareScrollView>
      {showExitConfirmation ? (
        <FormExitConfirmation
          confirmTrackingData={{ name: 'credit_request_screen.confirm_exit' }}
          message={i18n.t('exit_confirmation.message')}
          onConfirm={() => {
            setShowExitConfirmation(false);
            const cb = pendingExitCallback;
            setPendingExitCallback(null);
            cb?.();
          }}
          onSkip={() => {
            setShowExitConfirmation(false);
            setPendingExitCallback(null);
          }}
          skipTrackingData={{ name: 'credit_request_screen.cancel_exit' }}
          title={i18n.t('credit_request.screen.exit_confirmation.title')}
        />
      ) : null}
    </>
  );
});

const useStyles = createStyles((theme) => ({
  actionSheetContent: {
    paddingHorizontal: 0,
    paddingTop: 16,
  },
  addImageButton: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: theme.sk.radii.radiusSmall,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  borderlessInputContainer: {
    backgroundColor: theme.colors.transparent,
    borderColor: theme.colors.transparent,
    borderWidth: 0,
  },
  container: {
    alignItems: 'flex-start',
    flex: 1,
    flexDirection: 'row',
    marginBottom: 8,
  },
  content: {
    flexGrow: 1,
    justifyContent: 'space-between',
    paddingBottom: 36,
    paddingHorizontal: 24,
  },
  contentContainer: {
    flexGrow: 1,
    paddingTop: 24,
  },
  creditItemInfo: {
    flexDirection: 'row',
  },
  details: {
    alignSelf: 'center',
    flex: 1,
    marginLeft: 16,
  },
  disabledListItem: {
    opacity: 0.4,
  },
  flex: {
    flex: 1,
  },
  imagePreview: {
    borderColor: theme.sk.color.border.image,
    borderRadius: 8,
    borderWidth: 1,
  },
  ineligibleItemConfirmationHeader: {
    marginBottom: 16,
  },
  ineligibleItemConfirmationWrapper: {
    marginBottom: 16,
  },
  inputContainer: {
    borderColor: theme.colors.stroke,
  },
  invoiceItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  itemImagePreview: {
    borderRadius: theme.sk.radii.radiusSmall,
    height: 32,
    width: 32,
  },
  itemImagePreviews: {
    bottom: 16,
    flexDirection: 'row',
    gap: 8,
    left: 16,
    position: 'absolute',
  },
  itemSubtextContainer: {
    flexDirection: 'row',
    height: 56,
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  list: {
    marginHorizontal: -DEFAULT_PADDING,
    marginTop: 8,
  },
  listHeader: {
    paddingHorizontal: 24,
  },
  loadingWrapper: {
    flex: 1,
    justifyContent: 'center',
  },
  packSelectionContainer: {
    borderColor: theme.colors.stroke,
    borderRadius: 8,
    borderWidth: 1,
  },
  priceContainer: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  quantitySelector: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  separator: {
    marginHorizontal: 16,
  },
  textAreaContainer: {
    height: 'auto',
    minHeight:
      ADDITIONAL_NOTES_LINES * INPUT_HEIGHT + CONTAINER_PADDING_VERTICAL,
  },
  textRight: {
    textAlign: 'right',
  },
}));
